import { useEffect, useState } from 'react';
import { AlertTriangle, Code, Copy, Loader2, Monitor, Pencil, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useCreateTemplateMutation, useUpdateTemplateMutation } from '@/client/api/campaigns/campaigns.mutation';
import { NotificationTemplate } from '@/client/types/campaigns';
import EmailEditor, { EmailContent } from '@/client/components/admin/Email/EmailEditor';
import { generateIdempotencyKey } from '@/client/utils/requestSecurity';

// Full documents and table layouts come from an email designer, not the rich editor.
export const isDesignedHtml = (html?: string | null) => /<(!doctype|html|table)\b/i.test(html ?? '');

export const copyText = async (text: string, what: string) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${what} copied`, { richColors: true });
  } catch {
    toast.error(`Could not copy ${what.toLowerCase()}`, { richColors: true });
  }
};

/** Segmented control: a row of options, one active. */
function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string; icon?: React.ReactNode }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="inline-flex rounded-md border border-gray-200 bg-gray-50 p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`inline-flex items-center gap-1.5 rounded px-3 py-1 text-xs font-medium transition-colors ${
            value === o.value ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'
          }`}
        >
          {o.icon}
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Sandboxed render of an email at desktop or phone width. */
export function EmailFrame({ html, height = 'h-full' }: { html: string; height?: string }) {
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  return (
    <div className={`flex min-h-0 flex-col gap-2 ${height}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-gray-500">Preview</span>
        <Segmented
          value={device}
          onChange={setDevice}
          options={[
            { value: 'desktop', label: 'Desktop', icon: <Monitor className="h-3.5 w-3.5" /> },
            { value: 'mobile', label: 'Phone', icon: <Smartphone className="h-3.5 w-3.5" /> },
          ]}
        />
      </div>
      <div className="flex min-h-0 flex-1 justify-center overflow-auto rounded-md border border-gray-200 bg-gray-100 p-3">
        {html.trim() ? (
          <iframe
            title="Email preview"
            sandbox=""
            srcDoc={html}
            className="h-full min-h-[480px] rounded bg-white shadow-sm transition-[width]"
            style={{ width: device === 'desktop' ? '100%' : 375, maxWidth: '100%' }}
          />
        ) : (
          <p className="self-center text-sm text-gray-400">No email copy yet.</p>
        )}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Preview
// --------------------------------------------------------------------------- //
export function TemplatePreviewDialog({
  template,
  canWrite,
  onClose,
  onEdit,
  onDuplicate,
}: {
  template: NotificationTemplate | null;
  canWrite: boolean;
  onClose: () => void;
  onEdit: (t: NotificationTemplate) => void;
  onDuplicate: (t: NotificationTemplate) => void;
}) {
  const [tab, setTab] = useState<'email' | 'text' | 'sms'>('email');
  useEffect(() => setTab('email'), [template?.ntmplid]);
  if (!template) return null;
  const html = template.email_html ?? '';
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex h-[90vh] max-w-5xl flex-col">
        <DialogHeader>
          <DialogTitle>{template.name || template.name_system}</DialogTitle>
          <DialogDescription className="font-mono text-xs">
            #{template.ntmplid} · {template.name_system} · {template.locale || 'en_GB'}
          </DialogDescription>
        </DialogHeader>
        <Segmented
          value={tab}
          onChange={setTab}
          options={[
            { value: 'email', label: 'Email' },
            { value: 'text', label: 'Plain text' },
            { value: 'sms', label: 'SMS' },
          ]}
        />
        <div className="min-h-0 flex-1">
          {tab === 'email' && <EmailFrame html={html} />}
          {tab === 'text' && (
            <pre className="h-full overflow-auto whitespace-pre-wrap rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-800">
              {template.email_text || 'No plain-text version.'}
            </pre>
          )}
          {tab === 'sms' && (
            <div className="rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-800">
              {template.sms_text || 'No SMS copy.'}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="outline" disabled={!html} onClick={() => copyText(html, 'HTML')} className="gap-2">
            <Code className="h-4 w-4" />
            Copy HTML
          </Button>
          {canWrite && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onDuplicate(template)} className="gap-2">
                <Copy className="h-4 w-4" />
                Duplicate
              </Button>
              <Button onClick={() => onEdit(template)} className="gap-2">
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --------------------------------------------------------------------------- //
// Editor (create, edit, duplicate)
// --------------------------------------------------------------------------- //
export type EditorState =
  | { mode: 'create' }
  | { mode: 'edit'; template: NotificationTemplate }
  | { mode: 'duplicate'; template: NotificationTemplate };

export function TemplateEditorDialog({ state, onClose }: { state: EditorState | null; onClose: () => void }) {
  const source = state && state.mode !== 'create' ? state.template : null;
  const editing = state?.mode === 'edit' ? state.template : null;

  const [nameSystem, setNameSystem] = useState('');
  const [name, setName] = useState('');
  const [locale, setLocale] = useState('en_GB');
  const [email, setEmail] = useState<EmailContent>({ html: '', text: '' });
  const [smsText, setSmsText] = useState('');
  const [channel, setChannel] = useState<'email' | 'sms'>('email');
  const [htmlMode, setHtmlMode] = useState(false);
  // Remounts the rich editor whenever its content is replaced from outside.
  const [editorKey, setEditorKey] = useState(0);
  const [initial, setInitial] = useState('');
  const [confirmClose, setConfirmClose] = useState(false);

  const create = useCreateTemplateMutation();
  const update = useUpdateTemplateMutation();
  const saving = create.isPending || update.isPending;

  useEffect(() => {
    if (!state) return;
    const dup = state.mode === 'duplicate';
    const ns = source ? (dup ? `${source.name_system}_copy` : source.name_system) : '';
    const nm = source ? (dup ? `${source.name || source.name_system} (copy)` : source.name ?? '') : '';
    const em = { html: source?.email_html ?? '', text: source?.email_text ?? '' };
    setNameSystem(ns);
    setName(nm);
    setLocale(source?.locale ?? 'en_GB');
    setEmail(em);
    setSmsText(source?.sms_text ?? '');
    setChannel('email');
    setHtmlMode(isDesignedHtml(em.html));
    setEditorKey((k) => k + 1);
    setInitial(JSON.stringify([ns, nm, em, source?.sms_text ?? '']));
  }, [state]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!state) return null;

  const dirty = JSON.stringify([nameSystem, name, email, smsText]) !== initial;
  const requestClose = () => (dirty ? setConfirmClose(true) : onClose());
  // A designed email in rich mode loses its layout on save — say so before it happens.
  const layoutAtRisk = !htmlMode && isDesignedHtml(source?.email_html) && channel === 'email';

  const handleSave = async () => {
    if (!nameSystem.trim()) return toast.error('System name is required', { richColors: true });
    if (!editing && !email.html.trim() && !email.text.trim() && !smsText.trim()) {
      return toast.error('Write the email or the SMS copy', { richColors: true });
    }
    // Cleared fields go back to NULL: an empty sms_text is not "no SMS copy".
    const copy = {
      name: name.trim() || null,
      email_html: email.html.trim() ? email.html : null,
      email_text: email.text.trim() || null,
      sms_text: smsText.trim() || null,
    };
    try {
      if (editing) {
        await update.mutateAsync({ ntmplid: editing.ntmplid, body: copy, idempotencyKey: generateIdempotencyKey() });
        toast.success('Template saved', { richColors: true });
      } else {
        await create.mutateAsync({
          body: {
            name_system: nameSystem.trim(),
            ...(locale.trim() ? { locale: locale.trim() } : {}),
            ...Object.fromEntries(Object.entries(copy).filter(([, v]) => v)),
          },
          idempotencyKey: generateIdempotencyKey(),
        });
        toast.success('Template created', { richColors: true });
      }
      onClose();
    } catch (error: any) {
      toast.error(error?.message || 'Save failed', { richColors: true });
    }
  };

  const title =
    state.mode === 'edit'
      ? `Edit template #${editing!.ntmplid}`
      : state.mode === 'duplicate'
        ? `Duplicate template #${source!.ntmplid}`
        : 'New template';

  return (
    <>
      <Dialog open onOpenChange={(o) => !o && requestClose()}>
        <DialogContent
          className="flex h-[92vh] max-w-7xl flex-col"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>
              Templates with email copy appear under &ldquo;Start from a template&rdquo; in Email &gt; Send.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-3 sm:grid-cols-3">
            <label className="space-y-1 text-xs text-gray-500">
              System name
              <input
                value={nameSystem}
                onChange={(e) => setNameSystem(e.target.value)}
                disabled={!!editing}
                placeholder="launch_waitlist_open"
                className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm text-gray-900 disabled:bg-gray-50 disabled:text-gray-500"
              />
            </label>
            <label className="space-y-1 text-xs text-gray-500">
              Display name
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Launch: waitlist, it's open"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900"
              />
            </label>
            <label className="space-y-1 text-xs text-gray-500">
              Locale
              <input
                value={locale}
                onChange={(e) => setLocale(e.target.value)}
                disabled={!!editing}
                placeholder="en_GB"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 disabled:bg-gray-50 disabled:text-gray-500"
              />
            </label>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <Segmented
              value={channel}
              onChange={setChannel}
              options={[
                { value: 'email', label: 'Email' },
                { value: 'sms', label: 'SMS' },
              ]}
            />
            {channel === 'email' && (
              <Segmented
                value={htmlMode ? 'html' : 'rich'}
                onChange={(v) => {
                  setEditorKey((k) => k + 1);
                  setHtmlMode(v === 'html');
                }}
                options={[
                  { value: 'rich', label: 'Rich text' },
                  { value: 'html', label: 'HTML source', icon: <Code className="h-3.5 w-3.5" /> },
                ]}
              />
            )}
          </div>

          {layoutAtRisk && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              This is a designed email. The rich-text editor removes its layout, and saving from here replaces the
              designed version. Switch to HTML source to keep it.
            </div>
          )}

          <div className="min-h-0 flex-1">
            {channel === 'sms' ? (
              <div className="space-y-1">
                <textarea
                  value={smsText}
                  onChange={(e) => setSmsText(e.target.value)}
                  rows={4}
                  placeholder="SMS copy (optional)"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
                <p className="text-right text-xs text-gray-400">{smsText.length} characters</p>
              </div>
            ) : htmlMode ? (
              <div className="grid h-full min-h-0 gap-4 lg:grid-cols-2">
                <div className="flex min-h-0 flex-col gap-2">
                  <span className="text-xs font-medium text-gray-500">HTML</span>
                  <textarea
                    value={email.html}
                    onChange={(e) => setEmail((c) => ({ ...c, html: e.target.value }))}
                    spellCheck={false}
                    placeholder="<!DOCTYPE html>…"
                    className="min-h-[240px] flex-1 resize-none rounded-md border border-gray-300 px-3 py-2 font-mono text-xs leading-relaxed"
                  />
                  <span className="text-xs font-medium text-gray-500">Plain-text version</span>
                  <textarea
                    value={email.text}
                    onChange={(e) => setEmail((c) => ({ ...c, text: e.target.value }))}
                    rows={5}
                    placeholder="For mail apps that don't show HTML"
                    className="resize-none rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <EmailFrame html={email.html} />
              </div>
            ) : (
              <div className="h-full overflow-auto rounded-md border border-gray-300">
                <EmailEditor key={editorKey} value={email.html} onChange={setEmail} minHeight={360} />
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            {dirty && <span className="mr-auto self-center text-xs text-gray-500">Unsaved changes</span>}
            <Button variant="outline" onClick={requestClose}>
              Cancel
            </Button>
            <Button disabled={saving} onClick={handleSave} className="gap-2">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {editing ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard changes?</AlertDialogTitle>
            <AlertDialogDescription>Your edits to this template have not been saved.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmClose(false);
                onClose();
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
